import type { Plugin, Chart } from 'chart.js';
import type { TVerticalDistanceUnit } from 'src/utils/interfaces';
import { TGateValues } from '../types';

/**
 * Cache key interface for tracking when to redraw the background
 */
interface CacheKey {
  gateId: number;
  ceilingAltitude: number;
  floorAltitude: number;
  convertTo: TVerticalDistanceUnit;
}

/**
 * Conversion settings interface for altitude conversion
 */
interface AltitudeConversionSettings {
  convertTo: TVerticalDistanceUnit;
  convertFrom: TVerticalDistanceUnit;
  returnValueType: 'string' | 'number';
}

/**
 * WeakMap to store cache keys for each chart instance
 * This avoids property name conflicts and linting issues
 */
const chartCacheMap = new WeakMap<Chart, CacheKey>();

/**
 * Chart.js plugin that draws a grey rectangular background on the chart
 * to visually represent the gate boundaries.
 *
 * The plugin reads gate dimensions from the chart options and draws
 * a semi-transparent grey rectangle at the specified coordinates.
 *
 * @example
 * // Register the plugin
 * ChartJS.register(gateBackgroundPlugin);
 *
 * // Use in chart options
 * const options = {
 * plugins: {
 * gateBackground: {
 * gate: GateType,
 * },
 * },
 * };
 *
 * @typedef {Object} GateBackgroundPluginOptions
 * @property {GateType} gate - Gate type
 * @property {AltitudeConversionSettings} conversionSettingsVertical - Settings for vertical unit conversion
 */
export const gateBackgroundPlugin: Plugin = {
  id: 'gateBackground',
  /**
   * Hook called after the chart is drawn.
   * Draws a grey rectangle representing the gate boundaries.
   * Uses gate geometry ceilingAltitude and floorAltitude for Y-axis boundaries
   * and spans the full X-axis range.
   *
   * @param {import('chart.js').Chart} chart - The Chart.js instance
   * @returns {void}
   */
  afterDraw: (chart: Chart) => {
    const pluginOptions = (chart.options.plugins as Record<string, unknown>)?.gateBackground as {
      gateId: number;
      gateValues: string | null;
      conversionSettingsVertical?: string | null;
      conversionSettings?: string | null;
    } | undefined;

    if (!pluginOptions) {
      return;
    }

    const { gateId, gateValues, conversionSettingsVertical, conversionSettings } = pluginOptions;

    if (!gateValues || !conversionSettingsVertical || !conversionSettings) {
      return;
    }

    const gateObject = JSON.parse(gateValues) as TGateValues | null;
    const conversionSettingsVerticalObject = JSON.parse(conversionSettingsVertical) as AltitudeConversionSettings;

    if (!gateObject) {
      return;
    }

    const { ceilingAltitude, floorAltitude, gateLeft, gateRight} = gateObject;

    // Check cache to avoid unnecessary redraws
    const cachedKey = chartCacheMap.get(chart);
    const currentCacheKey: CacheKey = {
      gateId,
      ceilingAltitude,
      floorAltitude,
      convertTo: conversionSettingsVerticalObject.convertTo,
    };

    // Only skip if cache matches exactly AND chart is not in a hover state
    // Chart.js redraws on hover, which clears the canvas, so we need to redraw the background
    const shouldSkip = cachedKey &&
      cachedKey.gateId === currentCacheKey.gateId &&
      cachedKey.ceilingAltitude === currentCacheKey.ceilingAltitude &&
      cachedKey.floorAltitude === currentCacheKey.floorAltitude &&
      cachedKey.convertTo === currentCacheKey.convertTo;

    // Always redraw the background - the cache is just for optimization of calculations
    // but Chart.js clears the canvas on hover, so we must redraw
    const ctx = chart.ctx;
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;
    // Y-axis: Use converted ceiling and floor altitudes
    const yTop = ceilingAltitude;
    const yBottom = floorAltitude;

    // Convert data coordinates to pixel coordinates
    const x1 = xScale.getPixelForValue(gateLeft);
    const x2 = xScale.getPixelForValue(gateRight);
    const y1 = yScale.getPixelForValue(yTop); // Note: Y is inverted in canvas
    const y2 = yScale.getPixelForValue(yBottom);

    // Draw grey rectangle
    ctx.save();
    ctx.fillStyle = 'rgba(128, 128, 128, 0.3)';
    ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
    ctx.restore();

    // Update cache only if values changed (for optimization)
    if (!shouldSkip) {
      chartCacheMap.set(chart, currentCacheKey);
    }
  },
};

export default gateBackgroundPlugin;
